import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { DrawerRegistryService } from '../../../shared/services/drawer-registry.service';
import {
  TicketAssignmentRule,
  TicketCannedResponse,
  TicketCategory,
  TicketCustomField,
  TicketEscalationRule,
  TicketPipeline,
  TicketPipelineStage,
  TicketPriority,
  TicketRootCause,
  TicketSlaPolicy,
  TicketSlaTarget,
  TicketStatus,
  TicketTeam,
  TicketTeamMember
} from '../models/tickets-config.model';

/**
 * Drawer services para los 5 catálogos planos del módulo Tickets.
 * Variante reducida de la skill `crm-module-distribution`: cada catálogo
 * tiene su propio drawer (form embebido) que se abre desde su list.
 *
 * Convención común:
 *   - state$ emite { isOpen, options: { id? } } — id => modo edit.
 *   - saved$ emite la entidad creada/actualizada (para que la list recargue).
 */

interface DrawerOptions {
  id?: number;
  /** Para sub-recursos: ID del master (pipelineId, teamId, slaPolicyId). */
  parentId?: number;
}

class BaseDrawerService<T> {
  private readonly registry = inject(DrawerRegistryService);
  protected readonly state$ = new BehaviorSubject<{ isOpen: boolean; options: DrawerOptions }>({
    isOpen: false,
    options: {}
  });
  protected readonly saved$$ = new Subject<T>();

  readonly state = this.state$.asObservable();

  open(options: DrawerOptions = {}): void {
    if (!this.state$.value.isOpen) this.registry.opened();
    this.state$.next({ isOpen: true, options });
  }

  close(): void {
    if (this.state$.value.isOpen) this.registry.closed();
    this.state$.next({ isOpen: false, options: {} });
  }

  notifySaved(entity: T): void {
    this.saved$$.next(entity);
  }
}

// ============================================================================

@Injectable({ providedIn: 'root' })
export class TicketsPriorityDrawerService extends BaseDrawerService<TicketPriority> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsStatusDrawerService extends BaseDrawerService<TicketStatus> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsCategoryDrawerService extends BaseDrawerService<TicketCategory> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsRootCauseDrawerService extends BaseDrawerService<TicketRootCause> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsCannedResponseDrawerService extends BaseDrawerService<TicketCannedResponse> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

// ============================================================================
// Master + sub-resource drawers (Fase 6)
// ============================================================================

@Injectable({ providedIn: 'root' })
export class TicketsPipelineDrawerService extends BaseDrawerService<TicketPipeline> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsPipelineStageDrawerService extends BaseDrawerService<TicketPipelineStage> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsTeamDrawerService extends BaseDrawerService<TicketTeam> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsTeamMemberDrawerService extends BaseDrawerService<TicketTeamMember> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsSlaPolicyDrawerService extends BaseDrawerService<TicketSlaPolicy> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsSlaTargetDrawerService extends BaseDrawerService<TicketSlaTarget> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

// ============================================================================
// Configuración admin avanzada (Fase 7)
// ============================================================================

@Injectable({ providedIn: 'root' })
export class TicketsCustomFieldDrawerService extends BaseDrawerService<TicketCustomField> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsAssignmentRuleDrawerService extends BaseDrawerService<TicketAssignmentRule> {
  readonly entitySaved$ = this.saved$$.asObservable();
}

@Injectable({ providedIn: 'root' })
export class TicketsEscalationRuleDrawerService extends BaseDrawerService<TicketEscalationRule> {
  readonly entitySaved$ = this.saved$$.asObservable();
}
